/**
 * Helper utility functions
 * General-purpose utilities used throughout the application
 */

/**
 * Generates an array of years for graduation year selection
 * @param {number} count - Number of years to generate
 * @returns {Array<number>} Array of years starting from current year
 */
export const generateYearOptions = (count = 6) => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => currentYear + i);
};

/**
 * Sanitizes form data by trimming strings and lowercasing email
 * @param {Object} formData - Raw form data
 * @returns {Object} Sanitized form data
 */
export const sanitizeFormData = (formData) => {
  const sanitized = { ...formData };

  // Lowercase and trim email
  if (sanitized.email) {
    sanitized.email = sanitized.email.trim().toLowerCase();
  }

  // Trim all string values
  Object.keys(sanitized).forEach((key) => {
    if (typeof sanitized[key] === 'string' && key !== 'wachtwoord' && key !== 'bevestigWachtwoord') {
      sanitized[key] = sanitized[key].trim();
    }
  });

  return sanitized;
};

/**
 * Prepares registration data based on account type
 * @param {Object} formData - Form data
 * @param {string} accountType - Account type ('student' or 'werkgever')
 * @returns {Object} Prepared registration data
 */
export const prepareRegistrationData = (formData, accountType) => {
  const sanitized = sanitizeFormData(formData);

  const baseData = {
    email: sanitized.email,
    wachtwoord: formData.wachtwoord, // Don't trim password
    accountType,
  };

  if (accountType === 'student') {
    return {
      ...baseData,
      naam: sanitized.naam,
    };
  }

  if (accountType === 'werkgever') {
    return {
      ...baseData,
      bedrijfsnaam: sanitized.bedrijfsnaam,
    };
  }

  return baseData;
};

/**
 * Gets appropriate error message based on HTTP status code
 * @param {number} status - HTTP status code
 * @param {Object} data - Response data object
 * @param {Object} defaultMessages - Object with default messages for each status
 * @returns {string} Error message
 */
export const getErrorMessage = (status, data, defaultMessages = {}) => {
  // If server provides specific error message, use it
  if (data?.error) return data.error;
  if (data?.message) return data.message;

  // Otherwise, use status-based defaults
  const statusMessages = {
    400: defaultMessages[400] || 'Ongeldige gegevens. Controleer je invoer.',
    401: defaultMessages[401] || 'Onjuiste inloggegevens.',
    403: defaultMessages[403] || 'Geen toegang.',
    404: defaultMessages[404] || 'Niet gevonden.',
    409: defaultMessages[409] || 'Dit e-mailadres is al geregistreerd.',
    500: defaultMessages[500] || 'Serverfout. Probeer het later opnieuw.',
  };

  return statusMessages[status] || 'Er ging iets mis. Probeer het opnieuw.';
};

/**
 * Delays execution for specified milliseconds
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} Promise that resolves after delay
 */
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Safely parses JSON response
 * @param {Response} response - Fetch API response
 * @returns {Promise<Object>} Parsed JSON or empty object
 */
export const safeJsonParse = async (response) => {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (error) {
    console.error('JSON parse error:', error);
    return {};
  }
};

/**
 * Checks if error is a network error
 * @param {Error} error - Error object
 * @returns {boolean} True if network error
 */
export const isNetworkError = (error) => {
  return (
    error.message === 'Failed to fetch' ||
    error.message === 'Network request failed' ||
    error.name === 'NetworkError'
  );
};

/**
 * Formats user data for display
 * @param {Object} user - User object from API
 * @returns {Object} Formatted user object
 */
export const formatUserData = (user) => {
  return {
    id: user.id,
    email: user.email,
    accountType: user.accountType,
    displayName: user.naam || user.bedrijfsnaam || 'Gebruiker',
    isVerified: user.isVerified || false,
  };
};

/**
 * Gets URL query parameter value
 * @param {string} param - Parameter name
 * @returns {string|null} Parameter value or null
 */
export const getQueryParam = (param) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

/**
 * Checks if an object has any values
 * @param {Object} obj - Object to check
 * @returns {boolean} True if object has values
 */
export const hasValues = (obj) => {
  return Object.keys(obj).length > 0;
};
